// Propuesta de alquiler de equipos (31-oct-2026), mismo diseño limpio que el trabajo, el presupuesto y la venta.
// Dos montos: el abono mensual (lo que se paga todos los meses) y el pago inicial por la instalación,
// con las instalaciones bonificadas como descuento. Abajo: qué incluye, cómo se ajusta el precio y las condiciones.
// Los valores se cargan SIN IVA (así se negocian con empresas) y acá se les suma el 21% para seguir la regla
// de la app: la tabla con IVA incluido y en los totales Neto + IVA + Total según la condición del cliente.
import { CONTENT_W, M } from '../theme.js';
import { datos, tabla, totales, colCliente, plata, rotulo, texto, espacio, F, K, IVA } from './base.js';

const conIva = (n) => Number(n || 0) * (1 + IVA / 100);
const pct = (n) => `${String(n).replace('.', ',')}%`;

// Lista con viñetas debajo de un rótulo (incluye / condiciones)
function lista(doc, ctx, y, titulo, items) {
    const limpios = items.map(s => String(s || '').trim()).filter(Boolean);
    if (!limpios.length) return y;
    doc.setFontSize(F.texto);
    const bloques = limpios.map(s => ({ s, n: doc.splitTextToSize(s, CONTENT_W - 4).length }));
    y = espacio(doc, ctx, y, 6 + Math.min(3, bloques[0].n) * 3.9);
    rotulo(doc, titulo, M, y);
    y += 5;
    bloques.forEach(({ s, n }) => {
        y = espacio(doc, ctx, y, n * 3.9);
        texto(doc, '•', M, y, { color: K.rojo, negrita: true });
        texto(doc, s, M + 4, y, { ancho: CONTENT_W - 4 });
        y += n * 3.9 + 0.8;
    });
    return y + 3;
}

/**
 * a = {
 *   cantidad, equipo,               // 5, 'Dispenser de agua frío/calor'
 *   abonoNeto,                      // por equipo y por mes, sin IVA
 *   instalacionNeto, bonificadas,   // valor por instalación sin IVA y cuántas se bonifican
 *   contratoMeses,                  // plazo mínimo
 *   ajusteMeses, indice,            // cada cuántos meses se ajusta y con qué índice ('IPC (INDEC)')
 *   ejemploPct,                     // % de ejemplo para mostrar cómo queda el ajuste
 *   validezDias, diaPagoHasta,      // 15 días, se paga del 1 al 10
 *   incluye: [], condicionesExtra: [], observaciones
 * }
 */
// Versión corta (31-oct-2026): para la primera respuesta a quien pide precio. Solo el abono, la instalación
// en un renglón, qué incluye en tres líneas y las condiciones en una. El detalle queda para el contrato.
function alquilerResumido(doc, { ctx, cliente, sede, y, a, cant, abonoU, abonoTotal }) {
    const cond = cliente?.condicionFiscal || cliente?.condicionIva;
    if (cliente?.nombre) y = datos(doc, y, colCliente(cliente, sede), null);
    y = tabla(doc, ctx, y, [{ titulo: 'Alquiler mensual' }, { titulo: 'Cant.', ancho: 14, derecha: true }, { titulo: 'Por equipo', ancho: 30, derecha: true }, { titulo: 'Importe', ancho: 30, derecha: true }],
        [[`${a.equipo || 'Dispenser de agua'}\nCon mantenimiento, filtros y soporte técnico`, String(cant), plata(abonoU), plata(abonoTotal)]]);
    const instU = conIva(a.instalacionNeto);
    const bonif = Math.min(cant, Math.max(0, Number(a.bonificadas || 0)));
    const inst = Number(a.instalacionNeto) > 0
        ? (bonif >= cant ? 'Instalación sin cargo' : `Instalación: ${bonif > 0 ? `bonificamos ${bonif} de ${cant}, ` : ''}pago único de ${plata(instU * (cant - bonif))}`)
        : null;
    y = totales(doc, ctx, y, { subtotal: abonoTotal, total: abonoTotal, condicionIva: cond, etiquetaTotal: 'Por mes', notas: [inst] });
    y = lista(doc, ctx, y + 2, 'Incluye', [
        'Instalación y puesta en marcha de los equipos.',
        `Limpieza cada 6 meses y cambio de filtro anual.`,
        'Técnico sin cargo ante cualquier falla, o reemplazo del equipo.',
    ]);
    doc.setFontSize(F.chico);
    const pie = `Contrato mínimo de ${a.contratoMeses} meses · Ajuste cada ${a.ajusteMeses || 6} meses por ${a.indice || 'IPC (INDEC)'} · Las condiciones completas van en el contrato.`;
    y = espacio(doc, ctx, y, 8);
    texto(doc, pie, M, y, { tam: F.chico, color: K.gris, ancho: CONTENT_W });
    return y + 6;
}

export function generarAlquilerV2(doc, { ctx, cliente, sede, y, a }) {
    const cant = Math.max(1, Number(a.cantidad || 1));
    const abonoU = conIva(a.abonoNeto);
    const abonoTotal = abonoU * cant;
    if (a.resumido) return alquilerResumido(doc, { ctx, cliente, sede, y, a, cant, abonoU, abonoTotal });

    const colAlquiler = {
        rotulo: 'Alquiler',
        nombre: `${cant} ${cant === 1 ? 'equipo' : 'equipos'}`,
        lineas: [a.equipo, `Contrato mínimo de ${a.contratoMeses} meses`, 'Servicio técnico y mantenimiento incluidos'],
    };
    // Sin cliente cargado (propuesta para mandar antes de darlo de alta): solo la columna del alquiler
    y = cliente?.nombre ? datos(doc, y, colCliente(cliente, sede), colAlquiler) : datos(doc, y, colAlquiler);

    // 1 · Abono mensual
    y = tabla(doc, ctx, y, [{ titulo: 'Abono mensual' }, { titulo: 'Cant.', ancho: 14, derecha: true }, { titulo: 'Por equipo', ancho: 30, derecha: true }, { titulo: 'Importe', ancho: 30, derecha: true }],
        [[`Alquiler ${a.equipo ? a.equipo.toLowerCase() : 'de equipo'}\nInstalación, mantenimiento, filtros y soporte técnico`, String(cant), plata(abonoU), plata(abonoTotal)]]);
    y = totales(doc, ctx, y, {
        subtotal: abonoTotal, total: abonoTotal, condicionIva: cliente?.condicionFiscal || cliente?.condicionIva,
        etiquetaTotal: 'Abono mensual', notas: [`Se paga por mes adelantado, del 1 al ${a.diaPagoHasta || 10} de cada mes`],
    });

    // 2 · Pago inicial: instalación, con las bonificadas como descuento
    if (Number(a.instalacionNeto) > 0) {
        const instU = conIva(a.instalacionNeto);
        const bonif = Math.min(cant, Math.max(0, Number(a.bonificadas || 0)));
        y = espacio(doc, ctx, y + 2, 40);
        y = tabla(doc, ctx, y, [{ titulo: 'Pago inicial' }, { titulo: 'Cant.', ancho: 14, derecha: true }, { titulo: 'Unitario', ancho: 30, derecha: true }, { titulo: 'Importe', ancho: 30, derecha: true }],
            [['Instalación y puesta en marcha', String(cant), plata(instU), plata(instU * cant)]]);
        y = totales(doc, ctx, y, {
            subtotal: instU * cant,
            descuentos: bonif > 0 ? [{ label: `Bonificación ${bonif} ${bonif === 1 ? 'instalación' : 'instalaciones'}`, monto: instU * bonif }] : [],
            total: instU * (cant - bonif), condicionIva: cliente?.condicionFiscal || cliente?.condicionIva,
            etiquetaTotal: 'Pago inicial (una sola vez)',
        });
    }

    y = lista(doc, ctx, y + 2, 'Qué incluye', a.incluye?.length ? a.incluye : [
        'Instalación: llevamos los equipos, los conectamos y los dejamos funcionando.',
        'Mantenimiento preventivo cada 6 meses (limpieza y revisión) y cambio de filtro una vez al año.',
        'Soporte técnico ante cualquier falla, sin costo. Si un equipo no se puede reparar en el lugar, lo reemplazamos.',
        'Un solo abono mensual con factura.',
    ]);

    // Ajuste: una sola frase (31-oct-2026, Lucas: sin ejemplo en números). ejemploPct lo agrega si se carga.
    const cada = Number(a.ajusteMeses || 6);
    const ej = Number(a.ejemploPct || 0);
    y = lista(doc, ctx, y, 'Actualización del precio', [
        `El abono se actualiza cada ${cada} meses según la inflación acumulada de ese período, medida por el ${a.indice || 'IPC (INDEC)'}.`,
        ej > 0 ? `Ejemplo: si la inflación acumulada del período fue ${pct(ej)}, el abono por equipo pasa de ${plata(a.abonoNeto)} a ${plata(Number(a.abonoNeto || 0) * (1 + ej / 100))} + IVA.` : null,
    ]);

    y = lista(doc, ctx, y, 'Condiciones', [
        `Plazo mínimo de ${a.contratoMeses} meses; después sigue mes a mes.`,
        'Los equipos son propiedad de Dispenser La Tienda: se entregan en alquiler y se devuelven al terminar el contrato.',
        'Roturas por golpes, mal uso o manipulación de terceros se presupuestan aparte.',
        'Se pueden sumar equipos en cualquier momento al mismo abono.',
        `Si se da de baja antes de los ${a.contratoMeses} meses, se abonan los meses que faltan o se acuerda con la empresa.`,
        ...(a.condicionesExtra || []),
    ]);
    if (String(a.observaciones || '').trim()) y = lista(doc, ctx, y, 'Observaciones', [a.observaciones]);
    return y;
}
